import { Browser } from '@capacitor/browser'
import { App, type URLOpenListenerEvent } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import type { PlatformAdapter } from 'headlo-auth'

// PlatformAdapter for Capacitor — plugged into HeadloProviderV2 so its
// OAuth flow uses the OS's in-app browser (SFSafariViewController on iOS,
// Chrome Custom Tabs on Android) instead of the WebView.
//
// Why not the WebView: the WebView is a controlled surface — cookies don't
// share with Safari, screenshots aren't blocked, and Apple has explicitly
// rejected apps that put OAuth flows inside their own WebView (App Store
// Review Guideline 4.5.4 for password managers, but the pattern generalises).
// The system in-app browser is the RFC 8252 recommendation for native OAuth.
//
// Flow:
//   1. HeadloProviderV2 calls openAuthUrl(url) → Browser.open(url)
//   2. OS shows the browser sheet; user completes sign-in on
//      auth.headlo.com
//   3. auth.headlo.com 302-redirects to
//      https://auth.headlo.com/oauth/mobile-callback?code=...&state=...
//   4. iOS/Android Universal Link / App Link intercepts, launches this app
//      with that URL
//   5. Capacitor App plugin fires 'appUrlOpen' → our listener extracts
//      code + state, calls the handler HeadloProviderV2 subscribed with
//   6. Provider completes PKCE exchange in the background
//   7. Browser sheet auto-dismisses (via Browser.close in the handler)
//
// The redirectUri MUST be the exact URL that (a) matches allowed_redirect_uris
// on the publishable key AND (b) the mobile app's Universal Link declaration.
// Change either side without changing the other → OAuth flow breaks silently
// (user sees browser, signs in, browser stays open showing the fallback
// mobile-callback page because the OS didn't intercept).

const REDIRECT_URI = (() => {
  // Universal Link on real devices. In web dev (running in a desktop
  // browser via `npm run dev`), Universal Links don't exist — fall back
  // to a URL the browser can navigate to and pull code/state from.
  // Same auth.headlo.com/oauth/mobile-callback URL either way; only
  // the interception mechanism differs.
  if (Capacitor.isNativePlatform()) {
    return 'https://auth.headlo.com/oauth/mobile-callback'
  }
  // Web dev fallback — matches the local worker route registered above.
  return `${window.location.origin}/oauth/mobile-callback`
})()

export function createCapacitorAuthAdapter(): PlatformAdapter {
  // Handlers registered by HeadloProviderV2 via subscribeToCallback. Kept
  // as a ref-like set so multiple provider instances (shouldn't happen,
  // but shouldn't crash either) can coexist.
  const handlers = new Set<(params: { code: string; state: string }) => void>()

  // One-time listener on App URL-open events. Fires when iOS/Android
  // opens the app with a Universal Link (or a custom scheme in future).
  // Filter to just our callback URL so unrelated deep links don't reach
  // the auth handlers.
  App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
    let parsed: URL
    try { parsed = new URL(event.url) }
    catch { return }

    // Match either the prod Universal Link path or the web-dev localhost
    // callback path. Both use the same query-string shape.
    if (parsed.pathname !== '/oauth/mobile-callback') return

    const code  = parsed.searchParams.get('code')
    const state = parsed.searchParams.get('state') ?? ''
    if (!code) {
      console.warn('[capacitor-auth-adapter] callback URL missing code', event.url)
      return
    }

    // Auto-dismiss the browser sheet — the OAuth flow is done, no reason
    // to leave it sitting on top of the app. Non-fatal if it fails (user
    // can dismiss manually).
    if (Capacitor.isNativePlatform()) {
      void Browser.close().catch(() => {})
    }

    // Fan out to every subscribed handler. In practice, one handler = the
    // HeadloProviderV2 subscription.
    for (const h of handlers) h({ code, state })
  })

  return {
    redirectUri: REDIRECT_URI,

    openAuthUrl: async (url: string) => {
      if (Capacitor.isNativePlatform()) {
        // presentationStyle: 'popover' keeps the app visible behind the
        // browser sheet on iPad and larger phones. Default is 'fullscreen'
        // which fully covers the app — either is fine, popover is a hint
        // of "you're still in Headlo."
        await Browser.open({ url, presentationStyle: 'popover' })
      } else {
        // Web-dev fallback: standard tab navigation. Whoever loads the
        // callback URL in the same tab hits our /oauth/mobile-callback
        // route which serves the auto-close HTML page; the JS on that
        // page would need to postMessage back — for web dev we just
        // navigate the SAME tab which loses the OAuth state. Cost of
        // web dev; on-device works fine.
        window.location.href = url
      }
    },

    subscribeToCallback: (handler) => {
      handlers.add(handler)

      // Web-dev bridge: HeadloAuthClientV2 skips reading window.location.search
      // for `?code=` when a platformAdapter is configured — its assumption is
      // that native adapters (Electron IPC, real Capacitor deep link) supply
      // the callback via subscribeToCallback and the app-shell URL is empty.
      // In Capacitor's WEB target, we opened the auth flow via
      // window.location.href = url, so the code lands in this tab's URL,
      // and there's no native intercept to fire the handler.
      //
      // Fix: on first subscribe in web mode, check window.location for the
      // code and synthesize the callback ourselves. Then clean the URL so a
      // reload doesn't try to consume the (now-used) code again.
      //
      // Skipped on native — the appUrlOpen listener above owns that path.
      if (!Capacitor.isNativePlatform()) {
        try {
          const params = new URLSearchParams(window.location.search)
          const code   = params.get('code')
          // state is OPTIONAL — HeadloProviderV2's callback consumer only
          // uses `code`, and the auth server's Google flow currently doesn't
          // round-trip a state param. Require code only; pass empty string
          // for state so the handler shape matches the type.
          const state  = params.get('state') ?? ''
          if (code) {
            // setTimeout(0) — the provider's subscribeToCallback runs SYNC
            // during init(); firing the handler synchronously here would
            // race the client's own initialization ordering. A macrotask
            // hop lets init() finish assigning _adapterUnsubscribe before
            // we invoke the callback (matches how native events fire —
            // always after the current tick).
            setTimeout(() => {
              handler({ code, state })
              // Clean the URL fully: replace with root path (drop
              // /oauth/mobile-callback + the query string). Otherwise:
              //   - Reload would re-hit our callback path, but with no code
              //     the bridge no-ops and the URL just looks wrong.
              //   - Users notice "localhost:5175/oauth/mobile-callback"
              //     lingering in the address bar even though sign-in
              //     completed. Feels broken even when it works.
              window.history.replaceState({}, '', '/')
            }, 0)
          }
        } catch {
          // window.location parse failure — nothing to bridge, real callback
          // (if it happens) still flows through appUrlOpen.
        }
      }

      return () => { handlers.delete(handler) }
    },
  }
}
